<template>
  <div v-if="isMall" class="mall-my-list">
    <header class="mall-my-list__head is-split">
      <div>
        <h1>문의 내역</h1>
        <p>상품 문의와 그 밖 문의를 여기서 작성하고, 남긴 글과 답변을 확인합니다.</p>
      </div>
      <router-link :to="writePath" class="mall-my-list__primary">문의하기</router-link>
    </header>

    <div v-if="loading" class="mall-my-list__empty">불러오는 중입니다…</div>
    <div v-else-if="loadError" class="mall-my-list__empty">
      <p>{{ loadError }}</p>
    </div>
    <div v-else-if="!displayRows.length" class="mall-my-list__empty">
      <p>아직 작성한 문의가 없습니다.</p>
      <router-link :to="writePath">문의하기</router-link>
    </div>
    <ul v-else class="mall-my-list__cards">
      <li v-for="row in displayRows" :key="row.key">
        <div class="mall-my-list__meta">
          <strong>{{ row.status === 'DONE' ? '답변완료' : '답변대기' }}</strong>
          <time>{{ row.date }}</time>
        </div>
        <router-link :to="row.to" class="mall-my-list__title-link">{{ row.title }}</router-link>
        <p v-if="row.sub" class="mall-my-list__sub">{{ row.sub }}</p>
      </li>
    </ul>
    <PortalPager v-model="page" :total-pages="totalPages" />
  </div>

  <div v-else class="portal-page board-page consult-board-list">
    <div class="portal-page__inner">
      <header class="portal-page__head consult-board-list__head">
        <div>
          <p class="portal-page__eyebrow">상담</p>
          <h1 class="portal-page__title">상담 신청 내역</h1>
          <p class="portal-page__desc">장기요양기관 운영 상담과 답변을 확인합니다.</p>
        </div>
        <router-link :to="writePath" class="portal-btn portal-btn--primary consult-board-list__write">
          상담 신청
        </router-link>
      </header>

      <div class="board-page__table portal-panel">
        <table class="board-table">
          <thead>
            <tr>
              <th scope="col" class="board-table__num">번호</th>
              <th scope="col">제목</th>
              <th scope="col" class="board-table__cat">상태</th>
              <th scope="col" class="board-table__date">작성일</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4" class="board-table__status">불러오는 중…</td>
            </tr>
            <tr v-else-if="loadError">
              <td colspan="4" class="board-table__status">{{ loadError }}</td>
            </tr>
            <tr v-else-if="!displayRows.length">
              <td colspan="4" class="board-table__status">
                <div class="consult-board-list__empty">
                  <p>아직 신청한 상담이 없습니다.</p>
                  <router-link :to="writePath" class="portal-btn portal-btn--primary">
                    상담 신청하기
                  </router-link>
                </div>
              </td>
            </tr>
            <tr v-for="row in displayRows" v-else :key="row.key">
              <td class="board-table__num">{{ row.num }}</td>
              <td>
                <router-link :to="row.to" class="board-table__title">
                  {{ row.title }}
                </router-link>
                <p v-if="row.sub" class="consult-board-list__sub">{{ row.sub }}</p>
              </td>
              <td class="board-table__cat">
                <span
                  class="consult-board-list__status"
                  :class="row.status === 'DONE' ? 'is-done' : 'is-wait'"
                >
                  {{ row.status === 'DONE' ? '답변완료' : '답변대기' }}
                </span>
              </td>
              <td class="board-table__date">{{ row.date }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <PortalPager v-model="page" :total-pages="totalPages" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PortalPager from '@/components/common/PortalPager.vue'
import { fetchMyConsultPosts } from '@/api/consultBoard'
import { fetchMyQnas, type MallQna } from '@/api/mallReview'
import { mallProductPath } from '@/constants/mall'
import { MALL_MY_CONSULT_WRITE_PATH, MALL_MY_CONSULTS_PATH } from '@/constants/mallMy'
import type { MyConsultListItem } from '@/types/consultBoard'

interface InquiryRow {
  key: string
  num: number
  title: string
  sub: string
  status: 'WAIT' | 'DONE'
  createdAt: string
  date: string
  to: string
}

const route = useRoute()
const isMall = computed(() => route.meta.layout === 'mall')
const listPath = computed(() => (isMall.value ? MALL_MY_CONSULTS_PATH : '/consult-board'))
const writePath = computed(() => (isMall.value ? MALL_MY_CONSULT_WRITE_PATH : '/consult-board/write'))
const items = ref<MyConsultListItem[]>([])
const productQnas = ref<MallQna[]>([])
const loading = ref(true)
const loadError = ref('')
const page = ref(1)
const totalPages = ref(1)
const total = ref(0)
const pageSize = 10

const mallRows = computed(() => {
  const consultRows = items.value.map((item) => ({
    key: `consult-${item.id}`,
    title: item.title,
    sub: '',
    status: item.status,
    createdAt: item.createdAt,
    to: `${listPath.value}/${item.id}`,
  }))
  const qnaRows = productQnas.value.map((item) => ({
    key: `qna-${item.idx}`,
    title: item.question,
    sub: item.productName,
    status: (item.answer ? 'DONE' : 'WAIT') as 'WAIT' | 'DONE',
    createdAt: item.date,
    to: `${mallProductPath(item.productCode)}?tab=qna`,
  }))
  return [...consultRows, ...qnaRows].sort(
    (a, b) => rowTime(b.createdAt) - rowTime(a.createdAt),
  )
})

const displayRows = computed<InquiryRow[]>(() => {
  if (!isMall.value) {
    return items.value.map((item, index) => ({
      key: `consult-${item.id}`,
      num: total.value - (page.value - 1) * pageSize - index,
      title: item.title,
      sub: '',
      status: item.status,
      createdAt: item.createdAt,
      date: formatDate(item.createdAt),
      to: `${listPath.value}/${item.id}`,
    }))
  }
  const start = (page.value - 1) * pageSize
  return mallRows.value.slice(start, start + pageSize).map((row, index) => ({
    ...row,
    num: total.value - start - index,
    date: formatDate(row.createdAt),
  }))
})

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    if (isMall.value) {
      const [consults, qnas] = await Promise.all([
        fetchMyConsultPosts({ page: 1, limit: 100, channel: 'MALL' }),
        fetchMyQnas().catch(() => [] as MallQna[]),
      ])
      items.value = consults.items
      productQnas.value = qnas
      total.value = mallRows.value.length
      totalPages.value = Math.max(1, Math.ceil(total.value / pageSize))
      return
    }
    const data = await fetchMyConsultPosts({
      page: page.value,
      limit: pageSize,
      channel: 'PORTAL',
    })
    items.value = data.items
    total.value = data.pagination.total
    totalPages.value = data.pagination.totalPages
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : '목록을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function rowTime(value: string) {
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? 0 : time
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
}

watch(page, () => {
  if (!isMall.value) void load()
})

onMounted(() => {
  void load()
})
</script>

<style lang="scss" src="./board/BoardPage.scss"></style>
<style lang="scss" src="./mall/MallMyListPage.scss"></style>
<style lang="scss" src="./ConsultBoardListPage.scss"></style>
